This program has been developed by students from the bachelor Computer Science
at Utrecht University within the Software Project course. Copyright (c) Utrecht
University (Department of Information and Computing Sciences)

<template>
  <div class="basicBlock select-none instructionBlock">
    <p class="subheader top">{{ $t("game.instructions") }}</p>
    <!-- Statements, loops, function calls -->
    <div>
      <!-- Always visible-->
      <AsOp />
      <!-- Needed -->
      <While v-if="neededInstr['while']" />
      <IfElse v-if="neededInstr['ifElse']" />
      <!-- Extra -->
      <While v-if="!neededInstr['while'] && showExtra" />
      <IfElse v-if="!neededInstr['ifElse'] && showExtra" />
    </div>

    <!-- Operators -->
    <div class="grid grid-cols-4">
      <BinOp type="eq" v-if="neededInstr['eq']" />
      <BinOp type="neq" v-if="neededInstr['neq']" />
      <BinOp type="gt" v-if="neededInstr['gt']" />
      <BinOp type="lt" v-if="neededInstr['lt']" />
      <BinOp type="gte" v-if="neededInstr['gte']" />
      <BinOp type="lte" v-if="neededInstr['lte']" />

      <ParOp v-if="neededInstr['parOp']" />

      <!-- Extra -->
      <BinOp type="eq" v-if="!neededInstr['eq'] && showExtra" />
      <BinOp type="neq" v-if="!neededInstr['neq'] && showExtra" />
      <BinOp type="gt" v-if="!neededInstr['gt'] && showExtra" />
      <BinOp type="lt" v-if="!neededInstr['lt'] && showExtra" />
      <BinOp type="gte" v-if="!neededInstr['gte'] && showExtra" />
      <BinOp type="lte" v-if="!neededInstr['lte'] && showExtra" />

      <ParOp v-if="!neededInstr['parOp'] && showExtra" />
    </div>

    <!-- Extra operators -->
    <div class="grid grid-cols-4">
      <BinOp type="and" v-if="neededInstr['and']" />
      <BinOp type="or" v-if="neededInstr['or']" />
      <BinOp type="xor" v-if="neededInstr['xor']" />
      <UnOp type="not" v-if="neededInstr['not']" />

      <ArithOp type="add" v-if="neededInstr['add']" />
      <ArithOp type="subtract" v-if="neededInstr['subtract']" />
      <ArithOp type="multiply" v-if="neededInstr['multiply']" />
      <ArithOp type="divide" v-if="neededInstr['divide']" />
      <ArithOp type="mod" v-if="neededInstr['mod']" />
      <ArithOp type="power" v-if="neededInstr['power']" />

      <InfinOp v-if="neededInstr['inop']" />

      <!-- Extra -->
      <BinOp type="and" v-if="!neededInstr['and'] && showExtra" />
      <BinOp type="or" v-if="!neededInstr['or'] && showExtra" />
      <BinOp type="xor" v-if="!neededInstr['xor'] && showExtra" />
      <UnOp type="not" v-if="!neededInstr['not'] && showExtra" />

      <ArithOp type="add" v-if="!neededInstr['add'] && showExtra" />
      <ArithOp type="subtract" v-if="!neededInstr['subtract'] && showExtra" />
      <ArithOp type="multiply" v-if="!neededInstr['multiply'] && showExtra" />
      <ArithOp type="divide" v-if="!neededInstr['divide'] && showExtra" />
      <ArithOp type="mod" v-if="!neededInstr['mod'] && showExtra" />
      <ArithOp type="power" v-if="!neededInstr['power'] && showExtra" />

      <InfinOp v-if="!neededInstr['inop'] && showExtra" />
    </div>
    <div class="w-100 buttonsCenter spaceAbove" v-if="!answerMode">
      <div class="tooltip">
        <button class="button success big" @click="toggleExtras">
          {{ extraText }}
        </button>
        <div class="tooltipText top-6">
          <p>{{ getTranslation(extraTooltip) }}</p>
          <i></i>
        </div>
      </div>
    </div>

    <!-- Variables -->
    <div id="vars" class="grid grid-cols-2">
      <VarBlock
        v-for="v in varList"
        :key="v"
        :varData="v"
        ref="variableBlock"
        @updateContainers="$emit('updateContainers')"
      />
    </div>
    <div class="grid grid-cols-3 spaceAbove border-t-1">
      <select
        id="varType"
        class="blockEntry noRightBorder col-span-1"
        v-model="optionBlock"
        @change="createEnum"
      >
        <option value="num">{{ getTranslation("number") }}</option>
        <option value="string">{{ getTranslation("text") }}</option>
        <option value="bool">Bool</option>
        <option value="options">{{ getTranslation("options") }}</option>
      </select>
      <div class="blockEntry buttonsRight col-span-2">
        <input
          v-model="varName"
          class="input"
          maxLength="12"
          :placeholder="getTranslation('name')"
          autocomplete="off"
        />
        <div class="tooltip">
          <button
            id="addVar"
            class="button success"
            @click="
              addVariable(this.optionBlock, this.varName, this.optionsList)
            "
          >
            <img
              src="@/assets/images/icons/PlusGrey.svg"
              alt="Add variable icon"
            />
          </button>
          <div class="tooltipText left">
            <p>{{ $t("game.tooltips.addVariable") }}</p>
            <i></i>
          </div>
        </div>
      </div>
    </div>
    <div id="enumBlock" v-if="isEnum" class="instructionBlock">
      <!-- The block to use to make enum variables -->
      <div class="blockEntry buttonsRight">
        <input
          v-model="newOption"
          class="input"
          maxLength="15"
          :placeholder="getTranslation('option')"
          autocomplete="off"
        />
        <div class="tooltip">
          <button id="addOption" class="button success" @click="addOption">
            <img
              src="@/assets/images/icons/PlusGrey.svg"
              alt="Add enum option icon"
            />
          </button>
          <div class="tooltipText left">
            <p>{{ $t("game.tooltips.addOption") }}</p>
            <i></i>
          </div>
        </div>
      </div>
      <div
        v-for="(o, i) in optionsList"
        :key="i"
        class="blockEntry buttonsRight"
      >
        <input
          v-model="o.name"
          class="input"
          maxLength="15"
          autocomplete="off"
        />
        <div class="tooltip">
          <button
            id="removeOption"
            class="button warning"
            @click="removeOption(i)"
          >
            <img
              src="@/assets/images/icons/Min.svg"
              alt="Remove enum option icon"
            />
          </button>
          <div class="tooltipText left">
            <p>{{ $t("game.tooltips.removeOption") }}</p>
            <i></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
// import all components
import { Options, Vue } from "vue-class-component";
import { getWorldData } from "@/utils/apiCalls/WorldAPI";
import While from "@/components/game/psd/codeBlocks/While.vue";
import IfElse from "@/components/game/psd/codeBlocks/IfElse.vue";
import AsOp from "@/components/game/psd/operatorBlocks/AssignmentOperator.vue";
import ParOp from "@/components/game/psd/operatorBlocks/ParentheseOperator.vue";
import BinOp from "@/components/game/psd/operatorBlocks/BinaryOperator.vue";
import UnOp from "@/components/game/psd/operatorBlocks/UnaryOperator.vue";
import ArithOp from "@/components/game/psd/operatorBlocks/ArithmeticOperator.vue";
import InfinOp from "@/components/game/psd/operatorBlocks/InfiniteOperator.vue";
import VarBlock from "@/components/game/psd/variableBlocks/VariableBlock.vue";
import * as types from "@/utils/Types";
import json from "@/psdLang/lang.json";

@Options({
  data() {
    return {
      optionBlock: "num",
      varName: "",
      newOption: "",
      isEnum: false,
      optionsList: [] as types.option[],
      lang: null,
      json: json,
      neededInstr: {},
      showExtra: false,
      answerMode: false,
    };
  },
  beforeMount() {
    if (window.location.pathname == "/docent/levelmaker") {
      this.showExtra = true;
      this.answerMode = true;
      getWorldData("robbie");
    } else {
      this.setInstructionNeeds();
    }
    this.lang = this.$store.getters.getIDELanguage;
  },
  computed: {
    /**
     * Get variable list
     * @returns Variable list
     */
    varList() {
      let res = [];
      const c = this.$store.getters.getWorldData.constants;
      for (
        let i = 0;
        i < this.$store.getters.getWorldData.variables.length;
        i++
      ) {
        const v = this.$store.getters.getWorldData.variables[i];
        let x: types.varType = {
          name: v.name,
          type:
            v.type != "num" && v.type != "string" && v.type != "bool"
              ? "options"
              : v.type,
          options: [],
          worldVar: true,
        };
        if (x.type == "options") {
          for (let j = 0; j < c.length; j++) {
            if (c[j].type == v.type) x.options.push(c[j]);
          }
        }
        res.push(x);
      }
      return res;
    },
    /**
     * Switch dropdown arrow
     * @returns Dropdown arrow
     */
    extraText() {
      return this.showExtra ? "▲" : "▼";
    },
    /**
     * Switch extra tooltip
     * @returns Show or collapsed text
     */
    extraTooltip() {
      return this.showExtra ? "extraShow" : "extraCollapsed";
    },
  },
  methods: {
    //Set needed instructions
    setInstructionNeeds() {
      const needed =
        this.$store.getters.getLevelInfo.levelData.levelInstructions.needed;
      //Loop through needed functions
      for (let i = 0; i < needed.length; i++) {
        this.neededInstr[needed[i].name] = true;
      }
    },
    //Toggle extra instructions
    toggleExtras() {
      this.showExtra = !this.showExtra;
      this.$nextTick(() => this.$emit("updateContainers"));
    },
    /**
     * Add a variable to the variables that can be chosen
     * @param varType The type the new variable has
     * @param varName The variable name
     * @param optionsList A list containing all the possible options in case the variable is an enum
     */
    addVariable(
      this: {
        optionBlock: string;
        varName: string;
        optionsList: types.option[];
        varList: types.varType[];
        isEnum: boolean;
        checkOptionsList(): void;
        checkVariableName(name: string): boolean;
      },
      varType: string,
      varName: string,
      optionsList: types.option[]
    ) {
      // Clear the name input field
      this.varName = "";
      //Create a new variable with the input the user gave
      let newVar: types.varType = {
        name: varName,
        type: varType,
        options: [],
        worldVar: false,
      };
      //Check if the variable is unique
      if (
        varName == "" ||
        varName[0] == " " ||
        this.checkVariableName(varName)
      ) {
        console.log("Error: variable name is not valid");
      } else {
        if (varType == "options") {
          this.checkOptionsList();
          newVar.options = optionsList;
          this.optionsList = [];
          this.isEnum = false;
        }
        this.optionBlock = "num";
        this.varList.push(newVar);
      }
      (this as unknown as Vue).$forceUpdate();
    },
    /**
     * Check if the variable name already exists
     * @returns Boolean variable exists
     */
    checkVariableName: function (name: string) {
      for (let i = 0; i < this.varList.length; i++) {
        if (this.varList[i].name == name) return true;
      }
      return false;
    },
    //Allow the user to add options to an enum
    createEnum: function () {
      if (this.optionBlock == "options") this.isEnum = true;
      else this.isEnum = false;
    },
    //Add a new option to enum
    addOption: function () {
      let text = this.newOption;
      if (text[0] == " " || text == "" || this.checkOptionName(text)) {
        console.log("Error: option name is invalid");
      } else {
        this.optionsList.push({ name: text });
      }
      this.newOption = "";
    },
    //Remove option from the list
    removeOption: function (i: number) {
      this.optionsList.splice(i, 1);
    },
    /**
     * Check the validity of option name
     * @returns Boolean option name exists
     */
    checkOptionName: function (name: string, index: number) {
      for (let i = 0; i < this.optionsList.length; i++) {
        if (this.optionsList[i].name == name && index != i) return true;
      }
      return false;
    },
    //Check the options list for invalid options
    checkOptionsList: function () {
      let invalid: number[] = [];
      for (let i = 0; i < this.optionsList.length; i++) {
        if (
          this.optionsList[i].name.length == 0 ||
          this.checkOptionName(this.optionsList[i].name, i)
        ) {
          invalid.push(i);
        }
      }
      for (let n = 0; n < invalid.length; n++) {
        this.optionsList.splice(invalid[n] - n, 1);
      }
    },
    /**
     * Get translation of item
     * @param s String to be translated
     */
    getTranslation(s: string) {
      return this.json[this.lang][s];
    },
  },
  components: {
    While,
    IfElse,
    AsOp,
    ParOp,
    BinOp,
    UnOp,
    ArithOp,
    InfinOp,
    VarBlock,
  },
})
export default class InstructionBlock extends Vue {}
</script>
